import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bluetooth, Loader2, RefreshCw } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { toast } from "sonner";

interface Voucher {
  id: string;
  sender_id: string;
  receiver_id: string;
  amount_cents: number;
  currency: string;
  status: string;
  expires_at: string;
  created_at: string;
  settled_at: string | null;
  failure_reason: string | null;
}

const STATUSES = ["all", "pending", "accepted", "settled", "rejected", "expired"];

const AdminBluetooth = () => {
  const [filter, setFilter] = useState<string>("all");
  const [rows, setRows] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    let q = supabase
      .from("offline_vouchers")
      .select("id,sender_id,receiver_id,amount_cents,currency,status,expires_at,created_at,settled_at,failure_reason")
      .order("created_at", { ascending: false })
      .limit(200);
    if (filter !== "all") q = q.eq("status", filter);
    const { data, error } = await q;
    if (error) toast.error(error.message);
    setRows((data as Voucher[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { void load(); }, [filter]);

  const counts = rows.reduce<Record<string, number>>((acc, r) => {
    acc[r.status] = (acc[r.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bluetooth className="h-6 w-6" /> Bluetooth Transfers
          </h1>
          <p className="text-sm text-muted-foreground">
            Offline voucher monitor — pending, settled, failed.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <Button
            key={s}
            variant={filter === s ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(s)}
          >
            {s} {s !== "all" && counts[s] ? `(${counts[s]})` : ""}
          </Button>
        ))}
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left p-3">Voucher</th>
              <th className="text-left p-3">Sender → Receiver</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Created</th>
              <th className="text-left p-3">Expires</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-muted-foreground">
                  No vouchers
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t hover:bg-muted/30">
                <td className="p-3 font-mono text-xs">{r.id.slice(0, 8)}…</td>
                <td className="p-3 font-mono text-xs">
                  {r.sender_id.slice(0, 6)}… → {r.receiver_id.slice(0, 6)}…
                </td>
                <td className="p-3 text-right font-medium">
                  {formatMoney(r.amount_cents, r.currency)}
                </td>
                <td className="p-3">
                  <Badge
                    variant={
                      r.status === "settled"
                        ? "default"
                        : r.status === "rejected" || r.status === "expired"
                          ? "destructive"
                          : "secondary"
                    }
                  >
                    {r.status}
                  </Badge>
                  {r.failure_reason && (
                    <div className="text-xs text-muted-foreground mt-1">{r.failure_reason}</div>
                  )}
                </td>
                <td className="p-3 text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                <td className="p-3 text-xs text-muted-foreground">
                  {new Date(r.expires_at).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminBluetooth;
